import React, { useState } from "react";
import { useSelector, connect } from "react-redux";
import { Link } from "react-router-dom";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    withStyles,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Skeleton } from "@material-ui/lab";
import FastForwardOutlinedIcon from "@material-ui/icons/FastForwardOutlined";
import SearchIcon from "@material-ui/icons/Search";
import ClearAllIcon from "@material-ui/icons/ClearAll";
import InboxOutlinedIcon from "@material-ui/icons/InboxOutlined";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ConfirmationNumberOutlinedIcon from "@material-ui/icons/ConfirmationNumberOutlined";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import LabelOutlinedIcon from "@material-ui/icons/LabelOutlined";
import MonetizationOnOutlinedIcon from "@material-ui/icons/MonetizationOnOutlined";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloseIcon from "@material-ui/icons/Close";
import HttpService from "../services/httpService";
import { setSnackInfo } from "../redux/actions/appAction";
import { ROLENAME } from "../utils";

// Same brand palette and heading font as the Referral page, so the two read as one design.
const ORANGE = "#F47E44";
const ORANGE_DARK = "#c1531f";
const ORANGE_LIGHT = "#fff1ea";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`;
const GREEN = "#1e8449";
const GREEN_LIGHT = "#eafaf1";
const RED = "#c0392b";
const RED_LIGHT = "#fdecea";
const HEADING_FONT = "'Silka bold', 'Silka semiBold', sans-serif !important";

const SEARCH_TYPES = ["Lead No", "Project No", "Unique Lead No", "Customer Name", "Contact No"];
const COLUMN_COUNT = 11;

const formatAmount = (value) => `₹${(Number(value) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const initialsOf = (name = "") =>
    String(name).trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const BrandLinearProgress = withStyles({
    root: { height: 3, backgroundColor: ORANGE_LIGHT },
    bar: { backgroundColor: ORANGE },
})(LinearProgress);

const outlinedField = {
    '& .MuiOutlinedInput-root': { borderRadius: 12, backgroundColor: "#fff" },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: `${ORANGE}88` },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ORANGE },
    '& .MuiInputLabel-root.Mui-focused': { color: ORANGE_DARK },
};

const useStyles = makeStyles(() => ({
    page: { padding: 20, background: "#f6f7fb", minHeight: "100vh" },
    headerCard: {
        marginBottom: 20,
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 10px 28px rgba(196,83,31,0.2)",
        background: HEADER_GRADIENT,
        padding: "22px 26px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 16,
    },
    headerLeft: { display: "flex", alignItems: "center", gap: 16 },
    headerIconWrap: {
        width: 52,
        height: 52,
        borderRadius: "50%",
        background: "rgba(255,255,255,0.22)",
        border: "2px solid rgba(255,255,255,0.35)",
        boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    statChip: {
        background: "rgba(255,255,255,0.2)",
        color: "#fff",
        borderRadius: 20,
        padding: "7px 16px",
        fontWeight: 700,
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: 6,
        whiteSpace: "nowrap",
    },
    loadingBanner: {
        display: "flex", alignItems: "center", gap: 10,
        marginBottom: 14, padding: "10px 14px", borderRadius: 12,
        background: ORANGE_LIGHT, border: "1px solid #f6d3bd",
    },
    filterCard: {
        borderRadius: 16,
        boxShadow: "0 6px 20px rgba(17,24,39,0.06)",
        marginBottom: 20,
        background: "#fff",
        overflow: "hidden",
    },
    sectionLabel: {
        display: "flex",
        alignItems: "center",
        gap: 9,
        color: "#6b7280",
        fontSize: 12.5,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        marginBottom: 12,
    },
    iconBadge: {
        width: 28,
        height: 28,
        borderRadius: 9,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: "#fff",
        background: `linear-gradient(135deg, ${ORANGE_DARK}, ${ORANGE})`,
        boxShadow: "0 3px 9px rgba(244,126,68,0.38)",
    },
    filterRow: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14 },
    select: { width: 210, flex: "0 0 auto", ...outlinedField },
    searchField: { flex: "1 1 260px", maxWidth: 420, ...outlinedField },
    remarkField: outlinedField,
    submitBtn: {
        borderRadius: 10,
        textTransform: "none",
        fontWeight: 600,
        color: "#fff",
        background: `linear-gradient(135deg, ${ORANGE_DARK}, ${ORANGE})`,
        boxShadow: "none",
        padding: "8px 20px",
        "&:hover": { background: `linear-gradient(135deg, #a8441a, ${ORANGE_DARK})`, boxShadow: "0 6px 16px rgba(244,126,68,0.35)" },
        "&:disabled": { background: "rgba(0,0,0,0.12)", color: "rgba(0,0,0,0.38)" },
    },
    clearBtn: {
        borderRadius: 10,
        textTransform: "none",
        fontWeight: 600,
        color: "#6b7280",
        border: "1.5px solid #d8dee8",
        background: "#fff",
        padding: "8px 20px",
        "&:hover": { background: "#f5f6f8", borderColor: "#c3cad6" },
    },
    tableCard: { borderRadius: 16, overflow: "hidden", boxShadow: "0 6px 20px rgba(17,24,39,0.06)", background: "#fff" },
    tableWrap: { maxHeight: 650, overflow: "auto" },
    tableHeadCell: {
        // `!important`: MUI's stickyHeader rule paints its own background on th
        background: `${ORANGE_DARK} !important`,
        color: "#fff",
        fontWeight: 700,
        fontSize: 11.5,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        textAlign: "center",
        whiteSpace: "nowrap",
        borderBottom: "none",
    },
    tableRow: {
        "&:nth-of-type(odd)": { backgroundColor: "#FBFBFA" },
        "&:hover": { backgroundColor: ORANGE_LIGHT, boxShadow: `inset 3px 0 0 ${ORANGE}` },
        transition: "background-color .12s ease, box-shadow .12s ease",
    },
    // approver unavailable - the rows an Admin is most likely here for
    blockedRow: { boxShadow: `inset 3px 0 0 ${RED}` },
    tableCell: {
        padding: "10px",
        textAlign: "center",
        fontSize: 13.5,
        color: "#2b3648",
        borderBottom: "1px solid #f1f2f5",
    },
    rankCell: { color: "#9aa1ac", fontWeight: 800, fontVariantNumeric: "tabular-nums" },
    personCell: { display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
    personAvatar: {
        width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: ORANGE_LIGHT, color: ORANGE_DARK,
        fontSize: 11, fontWeight: 800,
    },
    amountCell: {
        fontWeight: 800,
        color: ORANGE_DARK,
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
    },
    subText: { fontSize: 11, color: "#9aa1ac", marginTop: 2 },
    leadLink: {
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        borderRadius: 7,
        minWidth: 0,
        background: ORANGE_LIGHT,
        color: ORANGE_DARK,
        fontWeight: 700,
        fontSize: 12.5,
        textTransform: "none",
        lineHeight: 1.6,
        "&:hover": { background: "#ffe3d2" },
    },
    typeChip: {
        background: ORANGE_LIGHT,
        color: ORANGE_DARK,
        fontWeight: 700,
        fontSize: 12,
    },
    stageFlow: { display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "center", alignItems: "center" },
    stageArrow: { fontSize: 13, color: "#c3cad6" },
    pendingWith: { textAlign: "left", minWidth: 210 },
    pendingStage: { fontWeight: 700, color: "#2b3648", fontSize: 13 },
    approverNote: { display: "flex", alignItems: "flex-start", gap: 4, fontSize: 12, marginTop: 3, lineHeight: 1.4 },
    emptyState: { padding: "60px 20px", textAlign: "center", color: "#9aa1ac" },
    emptyIconWrap: {
        width: 80,
        height: 80,
        borderRadius: "50%",
        background: ORANGE_LIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 14px",
    },
    dialogHeader: {
        background: HEADER_GRADIENT,
        color: "#fff",
        padding: "18px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
    },
    dialogHeaderTitle: { display: "flex", alignItems: "center", gap: 10 },
    dialogCloseBtn: {
        color: "rgba(255,255,255,0.9)",
        "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
    },
    infoRow: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 0",
        borderBottom: "1px solid #f0f1f5",
    },
    infoIconWrap: {
        width: 30,
        height: 30,
        borderRadius: "50%",
        background: ORANGE_LIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    statusCard: {
        margin: "16px 0",
        borderRadius: 12,
        padding: "12px 14px",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        fontSize: 13,
        fontWeight: 600,
    },
    fileRow: { display: "flex", alignItems: "center", gap: 4, marginTop: 6 },
    fileLink: { fontSize: 13, color: ORANGE_DARK, fontWeight: 600 },
    footNote: { marginTop: 14, fontSize: 12, color: "#9aa1ac" },
}));

const STAGE_CHIP = {
    "Approved": { background: GREEN_LIGHT, color: GREEN },
    "Sent for Approval": { background: RED_LIGHT, color: RED },
};

const StageChip = ({ stage }) => {
    const meta = STAGE_CHIP[stage.status] || { background: "#f3f4f6", color: "#6b7280" };
    return (
        <Tooltip title={`${stage.label}: ${stage.status || "Not Initiated"}`}>
            <Chip
                size="small"
                label={`${stage.short}${stage.status === "Approved" ? " ✓" : ""}`}
                style={{ ...meta, fontWeight: 700, fontSize: 11.5 }}
            />
        </Tooltip>
    );
};

const PersonCell = ({ name, note, classes }) => {
    if (!name) return <span style={{ color: "#9aa1ac" }}>N/A</span>;
    return (
        <div className={classes.personCell}>
            <span className={classes.personAvatar}>{initialsOf(name)}</span>
            <div style={{ textAlign: "left" }}>
                <strong style={{ fontWeight: 600 }}>{name}</strong>
                {note && <div className={classes.subText} style={note.color ? { color: note.color } : undefined}>{note.text}</div>}
            </div>
        </div>
    );
};

const InfoRow = ({ icon, label, value, classes }) => (
    <Box className={classes.infoRow}>
        <Box className={classes.infoIconWrap}>{icon}</Box>
        <Box style={{ flex: 1 }}>
            <Typography variant="caption" style={{ color: "#9aa1ac", display: "block" }}>{label}</Typography>
            <Typography component="div" style={{ fontWeight: 600, color: "#1f2937" }}>{value ?? "N/A"}</Typography>
        </Box>
    </Box>
);

const RefundApprovalBypass = (props) => {
    const classes = useStyles();
    const { userDetails } = useSelector((state) => state.user);
    const isAdmin = !!userDetails?.roles?.some((role) => role.name === ROLENAME.Admin);

    const [pendingRows, setPendingRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchType, setSearchType] = useState(SEARCH_TYPES[0]);
    const [search, setSearch] = useState("");
    // the last search that was run, reused to refresh the list after a bypass
    const [lastQuery, setLastQuery] = useState(null);

    const [selectedRow, setSelectedRow] = useState(null);
    const [remark, setRemark] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const getPendingApprovals = async (query) => {
        setLoading(true);
        try {
            const response = await HttpService.getRefundApprovalsForBypass(query);
            setPendingRows(response.data || []);
            setLastQuery(query);
        } catch (error) {
            props.setSnackInfo(error?.response?.data || "Could not load pending lost approvals", "error");
        }
        setLoading(false);
    };

    const handleSearch = () => {
        if (!search.trim()) {
            props.setSnackInfo(`Please enter the ${searchType} to search`, "warning");
            return;
        }
        getPendingApprovals({ searchType, search: search.trim() });
    };

    const handleClear = () => {
        setSearch("");
        setPendingRows([]);
        setLastQuery(null);
    };

    const blockedCount = pendingRows.filter((row) => !row.approverCheck?.available).length;

    const openBypassDialog = (row) => {
        setSelectedRow(row);
        setRemark("");
        setAttachments([]);
    };

    const closeBypassDialog = () => {
        if (submitting) return;
        setSelectedRow(null);
    };

    const handleUploadFiles = async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = "";
        if (!files.length) return;
        setUploading(true);
        try {
            const uploaded = [];
            for (const file of files) {
                const fd = new FormData();
                fd.append("file", file);
                const res = await HttpService.uploadFile(fd);
                uploaded.push({ name: file.name, url: res.data });
            }
            setAttachments((prev) => [...prev, ...uploaded]);
        } catch (error) {
            props.setSnackInfo("File upload failed, please try again", "error");
        }
        setUploading(false);
    };

    const removeAttachment = (url) => {
        setAttachments((prev) => prev.filter((file) => file.url !== url));
    };

    const submitBypass = async () => {
        if (!remark.trim()) {
            props.setSnackInfo("Please enter a remark", "warning");
            return;
        }
        setSubmitting(true);
        try {
            const res = await HttpService.bypassRefundApproval(selectedRow.leadId, {
                department: selectedRow.department,
                stageKey: selectedRow.currentStage.key,
                remark: remark.trim(),
                attachments: attachments.map((file) => file.url),
            });
            props.setSnackInfo(res?.data?.message || "Approval bypassed", "success");
            setSelectedRow(null);
            getPendingApprovals(lastQuery);
        } catch (error) {
            props.setSnackInfo(error?.response?.data || "Could not bypass the approval", "error");
            // the request may have moved on (approved / rejected / bypassed elsewhere) - show its latest state
            if (error?.response?.status === 409) {
                setSelectedRow(null);
                getPendingApprovals(lastQuery);
            }
        }
        setSubmitting(false);
    };

    const renderApprovalFlow = (row) => (
        <div className={classes.stageFlow}>
            {row.stages.map((stage, i) => (
                <React.Fragment key={stage.key}>
                    <StageChip stage={stage} />
                    {i < row.stages.length - 1 && <ArrowForwardIcon className={classes.stageArrow} />}
                </React.Fragment>
            ))}
        </div>
    );

    const renderPendingWith = (row) => {
        const { available, reason, approvers = [] } = row.approverCheck || {};
        return (
            <div className={classes.pendingWith}>
                <div className={classes.pendingStage}>{row.currentStage.label}</div>
                {available ? (
                    <div className={classes.approverNote} style={{ color: GREEN }}>
                        <CheckCircleOutlineIcon style={{ fontSize: 14, marginTop: 1 }} />
                        <span>{approvers.map((user) => user.name).join(", ")}</span>
                    </div>
                ) : (
                    <div className={classes.approverNote} style={{ color: RED }}>
                        <ErrorOutlineIcon style={{ fontSize: 14, marginTop: 1 }} />
                        <span>{reason}</span>
                    </div>
                )}
            </div>
        );
    };

    const renderTableBody = () => {
        if (loading) {
            return Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                    {Array.from({ length: COLUMN_COUNT }).map((__, c) => (
                        <TableCell key={c} className={classes.tableCell}>
                            <Skeleton variant="text" width={c === 0 ? 24 : "80%"} style={{ margin: "0 auto" }} />
                        </TableCell>
                    ))}
                </TableRow>
            ));
        }

        if (pendingRows.length === 0) {
            return (
                <TableRow>
                    <TableCell colSpan={COLUMN_COUNT} style={{ border: "none" }}>
                        <Box className={classes.emptyState}>
                            <Box className={classes.emptyIconWrap}>
                                {lastQuery
                                    ? <InboxOutlinedIcon style={{ fontSize: 36, color: ORANGE_DARK }} />
                                    : <SearchIcon style={{ fontSize: 36, color: ORANGE_DARK }} />}
                            </Box>
                            <Typography style={{ fontWeight: 600, color: "#7d8794" }}>
                                {lastQuery ? "No pending lost approvals found" : "Search to see pending approvals"}
                            </Typography>
                            <Typography variant="body2" style={{ color: "#a4abb5", marginTop: 4 }}>
                                {lastQuery
                                    ? "Nothing is waiting for approval on the leads matching this search."
                                    : "Search by Lead No, Project No, Unique Lead No, Customer Name or Contact No."}
                            </Typography>
                        </Box>
                    </TableCell>
                </TableRow>
            );
        }

        return pendingRows.map((row, index) => (
            <TableRow
                key={row.rowId}
                className={`${classes.tableRow} ${!row.approverCheck?.available ? classes.blockedRow : ""}`}
            >
                <TableCell className={`${classes.tableCell} ${classes.rankCell}`}>{index + 1}</TableCell>
                <TableCell className={classes.tableCell}>
                    <Tooltip title="Open lead details">
                        <Button size="small" component={Link} to={`/project-logs/${row.leadId}`} className={classes.leadLink}>
                            {row.lead_no || "N/A"}
                        </Button>
                    </Tooltip>
                </TableCell>
                <TableCell className={classes.tableCell}>
                    <PersonCell name={row.customerName} classes={classes} />
                </TableCell>
                <TableCell className={classes.tableCell}>{row.erpProjectNo || <span style={{ color: "#9aa1ac" }}>N/A</span>}</TableCell>
                <TableCell className={classes.tableCell}>{row.experienceCenterName || <span style={{ color: "#9aa1ac" }}>N/A</span>}</TableCell>
                <TableCell className={classes.tableCell}>
                    <Chip size="small" label={row.typeLabel} className={classes.typeChip} />
                    <div className={classes.subText}>{row.department}</div>
                </TableCell>
                <TableCell className={classes.tableCell}>
                    {row.type === "lostRefund"
                        ? <span className={classes.amountCell}>{formatAmount(row.refundAmount)}</span>
                        : <span style={{ color: "#9aa1ac" }}>-</span>}
                </TableCell>
                <TableCell className={classes.tableCell}>
                    {row.raisedBy ? (
                        <PersonCell
                            name={row.raisedBy.name}
                            note={!row.raisedBy.available ? { text: "Inactive", color: RED } : null}
                            classes={classes}
                        />
                    ) : (
                        <PersonCell name={row.assignedTo} note={row.assignedTo ? { text: "Lead owner" } : null} classes={classes} />
                    )}
                </TableCell>
                <TableCell className={classes.tableCell}>{renderApprovalFlow(row)}</TableCell>
                <TableCell className={classes.tableCell}>{renderPendingWith(row)}</TableCell>
                <TableCell className={classes.tableCell}>
                    <Tooltip title={row.canBypass ? `Skip ${row.currentStage.label} and send to ${row.nextStage.label}` : row.cannotBypassReason}>
                        <span>
                            <Button
                                size="small"
                                variant="contained"
                                className={classes.submitBtn}
                                style={{ padding: "5px 16px" }}
                                startIcon={<FastForwardOutlinedIcon style={{ fontSize: 16 }} />}
                                disabled={!row.canBypass}
                                onClick={() => openBypassDialog(row)}
                            >
                                Bypass
                            </Button>
                        </span>
                    </Tooltip>
                </TableCell>
            </TableRow>
        ));
    };

    if (!isAdmin) {
        return (
            <div className={classes.page}>
                <Box className={classes.tableCard}>
                    <Box className={classes.emptyState}>
                        <Box className={classes.emptyIconWrap}>
                            <ErrorOutlineIcon style={{ fontSize: 36, color: ORANGE_DARK }} />
                        </Box>
                        <Typography style={{ fontWeight: 600, color: "#7d8794" }}>Only Admin can access this page</Typography>
                    </Box>
                </Box>
            </div>
        );
    }

    return (
        <div className={classes.page}>
            {/* HEADER */}
            <Box className={classes.headerCard}>
                <Box className={classes.headerLeft}>
                    <Box className={classes.headerIconWrap}>
                        <FastForwardOutlinedIcon style={{ color: "#fff", fontSize: 26 }} />
                    </Box>
                    <Box>
                        <Typography style={{ color: "#fff", fontFamily: HEADING_FONT, fontSize: 21, margin: 0 }}>
                            Lost &amp; Refund Approval Bypass
                        </Typography>
                        <Typography style={{ color: "rgba(255,255,255,0.88)", fontSize: 13.5, marginTop: 4 }}>
                            Move a stuck lost approval to the next approver when the current one is inactive or unavailable
                        </Typography>
                    </Box>
                </Box>

                {lastQuery && (
                    <Box style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                        <Box className={classes.statChip}>
                            <InboxOutlinedIcon style={{ fontSize: 16 }} />
                            {pendingRows.length} Pending
                        </Box>
                        <Box className={classes.statChip}>
                            <ErrorOutlineIcon style={{ fontSize: 16 }} />
                            {blockedCount} Approver Unavailable
                        </Box>
                    </Box>
                )}
            </Box>

            {loading && (
                <Box className={classes.loadingBanner}>
                    <CircularProgress size={17} thickness={5} style={{ color: ORANGE_DARK }} />
                    <Typography style={{ fontSize: 13, fontWeight: 700, color: ORANGE_DARK }}>
                        Searching pending approvals…
                    </Typography>
                </Box>
            )}

            {/* SEARCH */}
            <Box className={classes.filterCard}>
                {loading && <BrandLinearProgress />}
                <Box style={{ padding: "18px 22px" }}>
                    <Typography className={classes.sectionLabel}>
                        <span className={classes.iconBadge}>
                            <SearchIcon style={{ fontSize: 15 }} />
                        </span>
                        Search
                    </Typography>
                    <Box className={classes.filterRow} style={{ opacity: loading ? 0.55 : 1, pointerEvents: loading ? "none" : "auto" }}>
                        <FormControl variant="outlined" size="small" className={classes.select}>
                            <InputLabel>Search By</InputLabel>
                            <Select value={searchType} onChange={(e) => setSearchType(e.target.value)} label="Search By">
                                {SEARCH_TYPES.map((option) => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            variant="outlined"
                            label={`Enter ${searchType}`}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            className={classes.searchField}
                        />
                        <Button variant="contained" className={classes.submitBtn} startIcon={<SearchIcon />} onClick={handleSearch}>
                            Search
                        </Button>
                        <Button variant="outlined" className={classes.clearBtn} startIcon={<ClearAllIcon />} onClick={handleClear}>
                            Clear
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* TABLE */}
            <Box className={classes.tableCard}>
                {loading && <BrandLinearProgress />}
                <TableContainer className={classes.tableWrap}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.tableHeadCell}>Sl No</TableCell>
                                <TableCell className={classes.tableHeadCell}>Lead No</TableCell>
                                <TableCell className={classes.tableHeadCell}>Customer Name</TableCell>
                                <TableCell className={classes.tableHeadCell}>Project No</TableCell>
                                <TableCell className={classes.tableHeadCell}>Exp-Center</TableCell>
                                <TableCell className={classes.tableHeadCell}>Type</TableCell>
                                <TableCell className={classes.tableHeadCell}>Refund Amount</TableCell>
                                <TableCell className={classes.tableHeadCell}>Raised By</TableCell>
                                <TableCell className={classes.tableHeadCell}>Approval Flow</TableCell>
                                <TableCell className={classes.tableHeadCell}>Pending With</TableCell>
                                <TableCell className={classes.tableHeadCell}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>{renderTableBody()}</TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* BYPASS DIALOG */}
            <Dialog open={!!selectedRow} onClose={closeBypassDialog} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: 16, overflow: "hidden" } }}>
                {selectedRow && (
                    <>
                        <Box className={classes.dialogHeader}>
                            <Box className={classes.dialogHeaderTitle}>
                                <FastForwardOutlinedIcon style={{ fontSize: 22 }} />
                                <Typography style={{ fontFamily: HEADING_FONT, fontSize: 17 }}>
                                    Bypass {selectedRow.currentStage.label} Approval
                                </Typography>
                            </Box>
                            <IconButton size="small" className={classes.dialogCloseBtn} onClick={closeBypassDialog} disabled={submitting}>
                                <CloseIcon style={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>
                        <DialogContent style={{ paddingTop: 8 }}>
                            <InfoRow
                                classes={classes}
                                icon={<ConfirmationNumberOutlinedIcon style={{ color: ORANGE_DARK, fontSize: 16 }} />}
                                label="Lead No"
                                value={selectedRow.lead_no}
                            />
                            <InfoRow
                                classes={classes}
                                icon={<PersonOutlineIcon style={{ color: ORANGE_DARK, fontSize: 16 }} />}
                                label="Customer"
                                value={selectedRow.customerName}
                            />
                            <InfoRow
                                classes={classes}
                                icon={<LabelOutlinedIcon style={{ color: ORANGE_DARK, fontSize: 16 }} />}
                                label="Type"
                                value={`${selectedRow.typeLabel} (${selectedRow.department})`}
                            />
                            {selectedRow.type === "lostRefund" && (
                                <InfoRow
                                    classes={classes}
                                    icon={<MonetizationOnOutlinedIcon style={{ color: ORANGE_DARK, fontSize: 16 }} />}
                                    label="Refund Amount"
                                    value={formatAmount(selectedRow.refundAmount)}
                                />
                            )}
                            <InfoRow
                                classes={classes}
                                icon={<ArrowForwardIcon style={{ color: ORANGE_DARK, fontSize: 16 }} />}
                                label="Route"
                                value={`${selectedRow.currentStage.label} (bypassed) → ${selectedRow.nextStage.label}`}
                            />

                            {selectedRow.approverCheck?.available ? (
                                <Box className={classes.statusCard} style={{ background: ORANGE_LIGHT, color: ORANGE_DARK }}>
                                    <ErrorOutlineIcon style={{ fontSize: 18 }} />
                                    <span>
                                        {selectedRow.currentStage.label} is available ({selectedRow.approverCheck.approvers.map((user) => user.name).join(", ")}). Bypassing will skip their approval.
                                    </span>
                                </Box>
                            ) : (
                                <Box className={classes.statusCard} style={{ background: RED_LIGHT, color: RED }}>
                                    <ErrorOutlineIcon style={{ fontSize: 18 }} />
                                    <span>{selectedRow.approverCheck?.reason}</span>
                                </Box>
                            )}

                            <TextField
                                label="Remark"
                                required
                                multiline
                                minRows={3}
                                variant="outlined"
                                fullWidth
                                value={remark}
                                onChange={(e) => setRemark(e.target.value)}
                                className={classes.remarkField}
                            />
                            <Box style={{ marginTop: 14 }}>
                                <input id="bypass-attachments" type="file" multiple style={{ display: "none" }} onChange={handleUploadFiles} />
                                <label htmlFor="bypass-attachments">
                                    <Button
                                        variant="outlined"
                                        component="span"
                                        className={classes.clearBtn}
                                        startIcon={uploading ? <CircularProgress size={14} style={{ color: ORANGE_DARK }} /> : <AttachFileIcon />}
                                        disabled={uploading || submitting}
                                    >
                                        {uploading ? "Uploading…" : "Upload Media (optional)"}
                                    </Button>
                                </label>
                                {attachments.map((file) => (
                                    <div key={file.url} className={classes.fileRow}>
                                        <a href={file.url} target="_blank" rel="noopener noreferrer" className={classes.fileLink}>{file.name}</a>
                                        <IconButton size="small" onClick={() => removeAttachment(file.url)} disabled={submitting}>
                                            <CloseIcon style={{ fontSize: 16 }} />
                                        </IconButton>
                                    </div>
                                ))}
                            </Box>
                            <Typography className={classes.footNote}>
                                The stage will be marked as Approved and a deal activity will be added with your name, the remark and any uploaded media.
                            </Typography>
                        </DialogContent>
                        <DialogActions style={{ padding: "14px 24px 20px" }}>
                            <Button onClick={closeBypassDialog} variant="outlined" className={classes.clearBtn} disabled={submitting}>
                                Cancel
                            </Button>
                            <Button
                                onClick={submitBypass}
                                variant="contained"
                                className={classes.submitBtn}
                                startIcon={submitting ? <CircularProgress size={14} style={{ color: "#fff" }} /> : <FastForwardOutlinedIcon />}
                                disabled={!remark.trim() || uploading || submitting}
                            >
                                Bypass &amp; Send to {selectedRow.nextStage.short}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </div>
    );
};

export default connect(null, { setSnackInfo })(RefundApprovalBypass);